import { GetOtsMatchQuery } from '@generated/gql/graphql';
import {
  FindStatus,
  IOTSCommunication,
  IOTSSwitchOption,
  OTSServiceAction,
  OrderType,
} from '@src/types/oneTouchSwitching';

export const getInitialOtsState = () => ({
  otsFindStatus: FindStatus.NOT_STARTED,
  otsComms: [] as IOTSCommunication[],
  otsHomePhoneSuffix: null,
  otsFieldErrors: [] as string[],
  otsMatchId: null,
  otsSwitchingReference: null,
  otsSwitchOptions: [] as IOTSSwitchOption[],
});

export const getProviderResetState = () => ({
  ...getInitialOtsState(),
  otsAccountReference: '',
  otsBroadbandReference: '',
  otsHomePhoneReference: null,
});

export const getValidOtsOptions = (
  matchData: GetOtsMatchQuery,
  orderType: OrderType
) => {
  const retrieveMatch = matchData?.broadbandOtsRetrieveMatch;
  return (
    retrieveMatch?.options?.filter((opt) => {
      const hasCeaseBroadband =
        opt?.broadband?.action === OTSServiceAction.Cease;
      const hasVoiceAction =
        opt?.homePhone?.action === OTSServiceAction.Cease ||
        opt?.homePhone?.action === OTSServiceAction.Port;

      return (
        hasCeaseBroadband ||
        (orderType === OrderType.BROADBAND_AND_VOICE && hasVoiceAction)
      );
    }) ?? []
  );
};

export const parseOtsMatchDetails = (
  matchData: GetOtsMatchQuery,
  validOptions: ReturnType<typeof getValidOtsOptions>
) => {
  const retrieveMatch = matchData?.broadbandOtsRetrieveMatch;

  const suffix =
    validOptions[0]?.homePhone?.identifier?.replace(/\*/g, '').slice(-2) ??
    null;

  const switchingReference =
    validOptions.length === 1
      ? (validOptions[0]?.switchOrderReference ?? null)
      : null;

  const communications =
    retrieveMatch?.implicationCommunications
      ?.filter((comm) => !!comm?.sentMethod)
      .map((comm) => ({
        name: comm?.sentMethod ?? '',
        value: comm?.sentTo ?? null,
      })) ?? [];

  const switchOptions = validOptions.map((opt) => ({
    switchingReference: opt?.switchOrderReference ?? '',
    broadbandAction: opt?.broadband?.action ?? OTSServiceAction.Unknown,
    homePhoneAction: opt?.homePhone?.action ?? OTSServiceAction.Unknown,
  }));

  return { suffix, switchingReference, communications, switchOptions };
};

export const parseOtsFieldErrors = (
  errors: (string | null | undefined)[] = [],
  orderType: OrderType
): string[] => {
  return errors.reduce<string[]>((acc, field) => {
    if (!field) return acc;

    switch (field) {
      case 'broadband':
        acc.push('broadbandReference');
        break;
      case 'homePhone':
        if (orderType === OrderType.BROADBAND_AND_VOICE) {
          acc.push('homePhoneReference');
        }
        break;
      case 'address':
        break;
      default:
        acc.push(field);
    }

    return acc;
  }, []);
};
